import { CyberboardR6Cdc } from "./cyberboardR6Cdc";
import { buildR6Download } from "./cyberboardR6Json";
import { frames } from "./cyberboardR6CdcProtocol";
import { diffR6Profile } from "./cyberboardR6Diff";
import { getR6ProductCompatibility } from "./cyberboardR6CdcProtocol";

function isSupportedR6Product(productId) {
  return getR6ProductCompatibility(productId).cdcSupported;
}

const READBACK_FEATURES = Object.freeze(["keyLayer", "macroKey", "pressHold"]);

function createReadbackStatus() {
  return READBACK_FEATURES.reduce((status, feature) => {
    status[feature] = { attempted: false, success: false };
    return status;
  }, {});
}

function markReadback(status, feature, success, attempted = true) {
  status[feature] = { attempted, success: attempted && success };
}

function parseJson(value) {
  if (typeof value === "string") return JSON.parse(value);
  return value;
}

function preserveProfilePages(data) {
  // 02 06 返回的是自定义页面数，不是完整 JSON 的 page_data 数量。
  return data;
}

function isEmptyKeyLayerValue(value) {
  if (typeof value !== "string") return false;
  const normalized = value.toUpperCase();
  return normalized === "#FFFFFFFF" || normalized === "#00000000";
}

function same(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

const PAGE_SYNC_FEATURES = [
  "pageControl",
  "pageText",
  "pageRgb",
  "axisRgb",
  "carLight",
];

// AM Master keeps 06 05 macro packets outside the 01 05/01 06 bulk commit.
const R6_MASTER_JSON_FEATURES = Object.freeze([
  "pageControl",
  "pageText",
  "pageRgb",
  "axisRgb",
  "keyFunctions",
  "keyLayer",
  "pressHold",
  "carLight",
]);

function clone(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

function isIgnoredSection(value) {
  return value === 2 || value === "2";
}

function isEnabledSection(value) {
  return value === true || value === 1 || value === "1";
}

function partialLedDownloadData(data) {
  const lightingApply = Boolean(data.__r6LightingApply);
  return {
    ...data,
    page_data: (data.page_data || []).map((page) => {
      const hasLedSection =
        !isIgnoredSection(page.frames?.valid) ||
        !isIgnoredSection(page.keyframes?.valid);
      const enabled = lightingApply || isEnabledSection(page.valid);
      if (enabled && (hasLedSection || lightingApply)) {
        return {
          ...page,
          ...(lightingApply ? { valid: 1 } : {}),
          ...(lightingApply && page.page_control
            ? { page_control: { ...page.page_control, valid: 1 } }
            : {}),
          frames: isIgnoredSection(page.frames?.valid)
            ? ignoredFrameCollection(page.frames)
            : page.frames,
          keyframes: isIgnoredSection(page.keyframes?.valid)
            ? ignoredFrameCollection(page.keyframes)
            : page.keyframes,
          ...(page.spotlight_frames
            ? {
                spotlight_frames: isIgnoredSection(page.spotlight_frames.valid)
                  ? ignoredFrameCollection(page.spotlight_frames)
                  : page.spotlight_frames,
              }
            : {}),
        };
      }
      return {
        ...page,
        valid: 2,
        frames: ignoredFrameCollection(page.frames),
        keyframes: ignoredFrameCollection(page.keyframes),
        ...(page.spotlight_frames
          ? { spotlight_frames: ignoredFrameCollection(page.spotlight_frames) }
          : {}),
      };
    }),
  };
}

function ignoredWordPage(value = {}) {
  return {
    ...value,
    valid: 2,
    word_len: 0,
    unicode: [],
  };
}

function ignoredFrameCollection(value = {}) {
  return {
    ...value,
    valid: 2,
    frame_num: 0,
    frame_data: [],
  };
}

function scopeFullJsonLightingData(data, pageFeatureScope) {
  return {
    ...data,
    page_data: (data.page_data || []).map((page, index) => {
      const pageIndex = Number(page?.page_index ?? index);
      const features = new Set(pageFeatureScope[pageIndex] || []);
      return {
        ...page,
        word_page: features.has("pageText")
          ? page.word_page
          : ignoredWordPage(page.word_page),
        frames: features.has("pageRgb")
          ? page.frames
          : ignoredFrameCollection(page.frames),
        keyframes: features.has("axisRgb")
          ? page.keyframes
          : ignoredFrameCollection(page.keyframes),
        ...(page.spotlight_frames
          ? {
              spotlight_frames: features.has("carLight")
                ? page.spotlight_frames
                : ignoredFrameCollection(page.spotlight_frames),
            }
          : {}),
      };
    }),
  };
}

export function prepareR6LightingApplyData(data, pageFeatureScope) {
  const scopedData = scopeFullJsonLightingData(data, pageFeatureScope);
  scopedData.page_data = scopedData.page_data.map((page) => {
    const features = new Set(pageFeatureScope[Number(page.page_index)] || []);
    const applyRgb = features.has("pageRgb");
    const applyAxisRgb = features.has("axisRgb");
    return {
      ...page,
      // Keep every page enabled; only the selected RGB sections are updated.
      valid: 1,
      ...(page.page_control
        ? { page_control: { ...page.page_control, valid: 1 } }
        : {}),
      frames: { ...page.frames, valid: applyRgb ? 1 : 2 },
      keyframes: { ...page.keyframes, valid: applyAxisRgb ? 1 : 2 },
    };
  });
  scopedData.__r6PartialImport = true;
  scopedData.__r6LightingApply = true;
  scopedData.key_layer = { ...scopedData.key_layer, valid: 2 };
  return scopedData;
}

function hasAllR6LedEffects(pageFeatureScope) {
  return (
    Object.values(pageFeatureScope || {}).filter((features) =>
      features.includes("pageRgb")
    ).length === 3
  );
}

function isPageInScope(pageIndex, pageIndexScope, pageFeatureScope) {
  if (pageFeatureScope !== undefined) {
    return Object.prototype.hasOwnProperty.call(pageFeatureScope, pageIndex);
  }
  return pageIndexScope === undefined || Number(pageIndex) === pageIndexScope;
}

function pageFeaturesForScope(
  featureScope,
  pageIndex,
  pageIndexScope,
  pageFeatureScope
) {
  const scoped = new Set();
  if (pageFeatureScope !== undefined) {
    (pageFeatureScope[pageIndex] || []).forEach((feature) =>
      scoped.add(feature)
    );
  } else if (isPageInScope(pageIndex, pageIndexScope)) {
    featureScope.forEach((feature) => scoped.add(feature));
  }
  // A per-page feature scope is authoritative; only an unscoped batch may
  // include page controls globally.
  if (
    featureScope.has("pageControl") &&
    pageIndexScope === undefined &&
    pageFeatureScope === undefined
  ) {
    scoped.add("pageControl");
  }
  return scoped;
}

function scopedDelta(delta, featureScope, pageIndexScope, pageFeatureScope) {
  const enabled = new Set(featureScope);
  const pageChanges = delta.pageChanges
    .filter((change) =>
      isPageInScope(change.pageIndex, pageIndexScope, pageFeatureScope)
    )
    .map((change) => {
      const scoped = { pageIndex: change.pageIndex, unsupported: [] };
      const pageFeatures = pageFeaturesForScope(
        enabled,
        change.pageIndex,
        pageIndexScope,
        pageFeatureScope
      );
      PAGE_SYNC_FEATURES.forEach((feature) => {
        if (pageFeatures.has(feature)) scoped[feature] = change[feature];
      });
      return scoped;
    })
    .filter((change) =>
      PAGE_SYNC_FEATURES.some((feature) => change[feature] === true)
    );

  const forcedPageScopes =
    pageFeatureScope !== undefined
      ? Object.keys(pageFeatureScope).map(Number)
      : pageIndexScope !== undefined
      ? [pageIndexScope]
      : [];
  forcedPageScopes.forEach((pageIndex) => {
    let pageChange = pageChanges.find(
      (change) => Number(change.pageIndex) === pageIndex
    );
    if (!pageChange) {
      pageChange = { pageIndex, unsupported: [] };
      pageChanges.push(pageChange);
    }
    const pageFeatures = pageFeaturesForScope(
      enabled,
      pageIndex,
      pageIndexScope,
      pageFeatureScope
    );
    PAGE_SYNC_FEATURES.forEach((feature) => {
      if (pageFeatures.has(feature)) pageChange[feature] = true;
    });
    pageChange.forceFullPage = true;
  });

  return {
    ...delta,
    layerStructureChanged:
      enabled.has("keyLayer") && delta.layerStructureChanged,
    keyLayerChanged: enabled.has("keyLayer") && delta.keyLayerChanged,
    keyChanges: enabled.has("keymap") ? delta.keyChanges : [],
    macroChanged: enabled.has("macro") && delta.macroChanged,
    pressHoldChanged: enabled.has("pressHold") && delta.pressHoldChanged,
    keyFunctionsChanged:
      enabled.has("keyFunctions") && delta.keyFunctionsChanged,
    pageChanges,
    unsupportedSections: [],
  };
}

function pageByIndex(pages = [], pageIndex) {
  return pages.find((page, index) => (page.page_index ?? index) === pageIndex);
}

function mergeScopedBaseline(
  previousData,
  nextData,
  verifiedData,
  scope,
  pageIndexScope,
  pageFeatureScope
) {
  const enabled = new Set(scope);
  const result = clone(previousData || {});

  if (enabled.has("keymap") || enabled.has("keyLayer")) {
    result.key_layer = clone(verifiedData.key_layer || nextData.key_layer);
  }
  if (enabled.has("macro")) {
    ["macro_key", "MACRO_key", "MACRO_key_num", "macro_key_num"].forEach(
      (key) => {
        if (key in nextData) {
          result[key] = clone(verifiedData[key] ?? nextData[key]);
        }
      }
    );
  }
  if (enabled.has("pressHold")) {
    ["tab_key", "tab_key_num"].forEach((key) => {
      if (key in nextData) {
        result[key] = clone(verifiedData[key] ?? nextData[key]);
      }
    });
  }
  if (enabled.has("keyFunctions")) {
    [
      "exchange_num",
      "exchange_key",
      "Fn_key_num",
      "Fn_key",
      "swap_key_num",
      "swap_key",
    ].forEach((key) => {
      if (key in nextData) result[key] = clone(nextData[key]);
    });
  }

  if (PAGE_SYNC_FEATURES.some((feature) => enabled.has(feature))) {
    const previousPages = result.page_data || [];
    const nextPages = nextData.page_data || [];
    const verifiedPages = verifiedData.page_data || [];
    const pageIndices = [
      ...new Set(
        [...previousPages, ...nextPages].map(
          (page, index) => page.page_index ?? index
        )
      ),
    ];
    result.page_data = pageIndices
      .map((pageIndex) => {
        const previousPage = pageByIndex(previousPages, pageIndex);
        const isScopedPage = isPageInScope(
          pageIndex,
          pageIndexScope,
          pageFeatureScope
        );
        const pageFeatures = pageFeaturesForScope(
          enabled,
          pageIndex,
          pageIndexScope,
          pageFeatureScope
        );
        const page = {
          ...(previousPage || {}),
        };
        const source =
          pageByIndex(verifiedPages, pageIndex) ||
          pageByIndex(nextPages, pageIndex) ||
          {};
        ["valid", "page_index", "lightness", "speed_ms", "color"].forEach(
          (key) => {
            if (pageFeatures.has("pageControl") && key in source) {
              page[key] = clone(source[key]);
            }
          }
        );
        if (
          isScopedPage &&
          pageFeatures.has("pageText") &&
          "word_page" in source
        ) {
          page.word_page = clone(source.word_page);
        }
        if (isScopedPage && pageFeatures.has("pageRgb") && "frames" in source) {
          page.frames = clone(source.frames);
        }
        if (
          isScopedPage &&
          pageFeatures.has("axisRgb") &&
          "keyframes" in source
        ) {
          page.keyframes = clone(source.keyframes);
        }
        if (
          isScopedPage &&
          pageFeatures.has("carLight") &&
          "spotlight_frames" in source
        ) {
          page.spotlight_frames = clone(source.spotlight_frames);
        }
        return page;
      })
      .filter(Boolean);
  }
  return result;
}

function requiresFullJsonLedDownload(deviceInfo, features = []) {
  if (
    !features.some((feature) =>
      ["pageRgb", "axisRgb", "carLight"].includes(feature)
    )
  ) {
    return false;
  }
  return getR6ProductCompatibility(deviceInfo?.productId).fullJsonSupported;
}

function restoreSkippedPageFeatures(previousData, nextData, pageChanges) {
  const previousPages = new Map(
    (previousData.page_data || []).map((page, index) => [
      Number(page?.page_index ?? index),
      page,
    ])
  );
  const changes = new Map(
    pageChanges.map((change) => [Number(change.pageIndex), change])
  );
  return {
    ...nextData,
    page_data: (nextData.page_data || []).map((page, index) => {
      const pageIndex = Number(page?.page_index ?? index);
      const previousPage = previousPages.get(pageIndex);
      const change = changes.get(pageIndex);
      if (!previousPage || !change) return page;
      const restored = { ...page };
      if (change.pageControl) {
        ["valid", "page_index", "lightness", "speed_ms", "color"].forEach(
          (key) => {
            restored[key] = clone(previousPage[key]);
          }
        );
      }
      if (change.pageText) restored.word_page = clone(previousPage.word_page);
      if (change.pageRgb) restored.frames = clone(previousPage.frames);
      if (change.axisRgb) restored.keyframes = clone(previousPage.keyframes);
      if (change.carLight) {
        restored.spotlight_frames = clone(previousPage.spotlight_frames);
      }
      return restored;
    }),
  };
}

function changedFrames(nextFrames = {}, previousFrames = {}) {
  const oldByIndex = new Map(
    (previousFrames.frame_data || []).map((frame, index) => [
      frame.frame_index ?? index,
      frame,
    ])
  );
  return (nextFrames.frame_data || []).filter((frame, index) => {
    const frameIndex = frame.frame_index ?? index;
    return !same(oldByIndex.get(frameIndex), frame);
  });
}

function pageDeltaData(
  data,
  previousData,
  pageChanges,
  features,
  pageIndexScope,
  pageFeatureScope
) {
  const changesByPage = new Map(
    pageChanges.map((change) => [change.pageIndex, change])
  );
  const previousPageByIndex = new Map(
    (previousData.page_data || []).map((page, index) => [
      page.page_index ?? index,
      page,
    ])
  );
  return {
    ...data,
    page_data: (data.page_data || []).map((page, index) => {
      const pageIndex = page.page_index ?? index;
      const isScopedPage = isPageInScope(
        pageIndex,
        pageIndexScope,
        pageFeatureScope
      );
      const pageFeatures = pageFeaturesForScope(
        new Set(features),
        pageIndex,
        pageIndexScope,
        pageFeatureScope
      );
      const forceFullPage =
        isScopedPage &&
        (pageIndexScope !== undefined || pageFeatureScope !== undefined);
      const change = changesByPage.get(pageIndex) || {};
      const previousPage = previousPageByIndex.get(pageIndex) || {};
      const sendPageText =
        pageFeatures.has("pageText") && (change.pageText || forceFullPage);
      const sendPageRgb =
        pageFeatures.has("pageRgb") && (change.pageRgb || forceFullPage);
      const sendAxisRgb =
        pageFeatures.has("axisRgb") && (change.axisRgb || forceFullPage);
      const pageRgbFrames = sendPageRgb
        ? forceFullPage
          ? page.frames?.frame_data || []
          : changedFrames(page.frames, previousPage.frames)
        : [];
      const axisRgbFrames = sendAxisRgb
        ? forceFullPage
          ? page.keyframes?.frame_data || []
          : changedFrames(page.keyframes, previousPage.keyframes)
        : [];
      const carLightFrames =
        pageFeatures.has("carLight") &&
        (change.carLight || forceFullPage) &&
        page.spotlight_frames
          ? forceFullPage
            ? page.spotlight_frames.frame_data || []
            : changedFrames(
                page.spotlight_frames,
                previousPage.spotlight_frames
              )
          : [];
      // 02 01 must describe sparse frames actually sent in this transaction.
      return {
        ...page,
        // Light pages are not readable on CB04; keep all profile pages enabled
        // instead of replaying possibly stale local disabled flags.
        valid: pageFeatures.has("pageControl") ? 1 : 2,
        word_page: {
          ...page.word_page,
          valid: sendPageText ? page.word_page?.valid : 2,
          word_len: sendPageText ? page.word_page?.word_len || 0 : 0,
        },
        frames: {
          ...page.frames,
          valid: sendPageRgb ? page.frames?.valid : 2,
          frame_num: pageRgbFrames.length,
          frame_data: pageRgbFrames,
        },
        keyframes: {
          ...page.keyframes,
          valid: sendAxisRgb ? page.keyframes?.valid : 2,
          frame_num: axisRgbFrames.length,
          frame_data: axisRgbFrames,
        },
        ...(page.spotlight_frames
          ? {
              spotlight_frames: {
                ...page.spotlight_frames,
                valid:
                  pageFeatures.has("carLight") &&
                  (change.carLight || forceFullPage)
                    ? page.spotlight_frames.valid
                    : 2,
                frame_num: carLightFrames.length,
                frame_data: carLightFrames,
              },
            }
          : {}),
      };
    }),
    key_layer: { ...data.key_layer, valid: 2 },
  };
}

function changedPressHoldFrames(previousItems = [], nextItems = []) {
  const before = new Map(previousItems.map((item) => [item.ph_key_data, item]));
  const after = new Map(nextItems.map((item) => [item.ph_key_data, item]));
  const changed = nextItems.filter(
    (item) => !same(before.get(item.ph_key_data), item)
  );
  previousItems.forEach((item) => {
    if (after.has(item.ph_key_data)) return;
    changed.push({
      ...item,
      first_key: "#00000000",
      second_key: "#00000000",
      interval_time: 0,
    });
  });
  return changed.map((item) => frames.pressHold(item));
}

function macroDataWithRemovedSlots(previousItems = [], nextData = {}) {
  const current = nextData.macro_key || [];
  const keys = new Set(current.map((item) => item.original_key));
  const cleared = previousItems
    .filter((item) => !keys.has(item.original_key))
    .map((item) => ({
      original_key: item.original_key,
      layer_key: [],
      intvel_ms: [],
    }));
  return { ...nextData, macro_key: [...current, ...cleared] };
}

function mergeKeyLayerData(baseData, readbackData, preserveEmptyValues = true) {
  const baseLayers = Array.isArray(baseData?.key_layer?.layer_data)
    ? baseData.key_layer.layer_data
    : [];
  return readbackData.map((readbackLayer, layerIndex) => {
    const baseLayer = baseLayers[layerIndex]?.layer || [];
    return {
      ...readbackLayer,
      layer: (readbackLayer.layer || []).map((value, keyIndex) =>
        preserveEmptyValues && isEmptyKeyLayerValue(value)
          ? baseLayer[keyIndex] || value
          : value
      ),
    };
  });
}

export class CyberboardR6Bridge {
  constructor(options = {}) {
    this.cdc = new CyberboardR6Cdc(options);
    this.handlers = {};
    this.currentData = null;
    this.deviceInfo = null;
    this.isFreshConnection = false;
    this.cdc.on("connected", (port) => {
      this.isFreshConnection = true;
      this.emit("connected", port);
    });
    this.cdc.on("disconnected", () => {
      this.isFreshConnection = true;
      this.currentData = null;
      this.deviceInfo = null;
      this.emit("disconnected");
    });
  }

  get isConnected() {
    return this.cdc.isConnected;
  }

  log(action, detail) {
    if (typeof this.cdc.log === "function") {
      this.cdc.log(action, detail);
    }
  }

  async trackFeature(feature, operation) {
    const startedAt = Date.now();
    try {
      const result = await operation();
      this.log("feature:success", {
        feature,
        elapsedMs: Date.now() - startedAt,
      });
      return result;
    } catch (error) {
      this.log("feature:failed", {
        feature,
        error: error.message,
        elapsedMs: Date.now() - startedAt,
      });
      throw error;
    }
  }

  on(event, handler) {
    if (!this.handlers[event]) this.handlers[event] = [];
    this.handlers[event].push(handler);
  }

  off(event, handler) {
    if (!this.handlers[event]) return;
    this.handlers[event] = handler
      ? this.handlers[event].filter((item) => item !== handler)
      : [];
  }

  emit(event, data) {
    (this.handlers[event] || []).forEach((handler) => handler(data));
  }

  async connect() {
    this.log("bridge.connect:start");
    try {
      return await this.cdc.connect({ acceptProduct: isSupportedR6Product });
    } catch (error) {
      this.log("bridge.connect:failed", { error: error.message });
      throw error;
    }
  }

  async connectExisting() {
    this.log("bridge.connectExisting:start");
    try {
      return await this.cdc.connectExisting({
        acceptProduct: isSupportedR6Product,
      });
    } catch (error) {
      this.log("bridge.connectExisting:failed", { error: error.message });
      throw error;
    }
  }

  async identify() {
    this.log("bridge.identify");
    const info = await this.cdc.identify();
    const compatibility = getR6ProductCompatibility(info.productId);
    if (!compatibility.cdcSupported) {
      throw new Error(`CDC 设备型号不匹配，检测到 ${info.productId}`);
    }
    return {
      ...info,
      productModel: compatibility.model,
      productFamily: compatibility.family,
      cdcSupported: compatibility.cdcSupported,
      audioSupported: compatibility.audioSupported,
      firmwareSupported: compatibility.firmwareSupported,
    };
  }

  async disconnect() {
    this.log("bridge.disconnect");
    return this.cdc.disconnect();
  }

  async resetKeyboard() {
    await this.cdc.enqueue(() => this.cdc.write(frames.deviceReset()));
    this.cdc.clearJsonTransactionBlock();
    this.currentData = null;
    this.log("keyboard-reset:sent", { command: "06 11" });
  }

  async enterTransportMode() {
    if (!this.isConnected) {
      throw new Error("R6 CDC 尚未连接，无法进入运输模式");
    }
    const response = await this.cdc.request(
      frames.enterTransportMode(),
      (frame) => frame[0] === 0x07 && frame[1] === 0x01
    );
    if (response[2] !== 0x01) {
      throw new Error(`进入运输模式失败，状态码 ${response[2]}`);
    }
    this.log("transport-mode:success", { command: "07 01" });
    return response;
  }

  async enterUpgradeMode() {
    if (!this.isConnected) {
      throw new Error("R6 CDC 尚未连接，无法进入升级模式");
    }
    const response = await this.cdc.enterUpgradeMode();
    // The keyboard re-enumerates as a Nordic DFU serial device after ACK.
    await new Promise((resolve) => setTimeout(resolve, 150));
    await this.disconnect();
    return response;
  }

  async downloadJSON(value) {
    try {
      const changes = parseJson(value);
      this.log("downloadJSON:start", {
        mode: Array.isArray(changes) ? "key-changes" : "rejected-profile-json",
        count: Array.isArray(changes) ? changes.length : undefined,
      });
      // 通用改键入口拒绝整份配置对象；完整下发只由 sendFullProfile 调用。
      if (!Array.isArray(changes)) {
        throw new Error("R6 完整配置请使用明确的导入/整配置下发入口");
      }
      const unsupported = changes.find((change) => change.msg_type !== 0);
      if (unsupported) {
        throw new Error("R6 当前尚未支持宏、按下保持和灯效下发");
      }
      if (!this.currentData?.key_layer?.layer_data) {
        throw new Error("R6 尚未完成设备配置同步，无法安全应用改键");
      }
      // 兼容数组请求先基于已确认快照生成差异，再复用 06 0B 写入路径。
      const nextData = clone(this.currentData);
      changes.forEach((change) => {
        const layerIndex = Number(change.layer_num);
        const rowIndex = Number(change.row_num);
        const columnIndex = Number(change.index);
        const layer = nextData.key_layer.layer_data[layerIndex]?.layer;
        const keyIndex = rowIndex * 25 + columnIndex;
        if (
          !Number.isInteger(layerIndex) ||
          layerIndex < 0 ||
          !Number.isInteger(rowIndex) ||
          rowIndex < 0 ||
          !Number.isInteger(columnIndex) ||
          columnIndex < 0 ||
          columnIndex >= 25 ||
          !Array.isArray(layer) ||
          keyIndex >= layer.length ||
          layer[keyIndex] !== change.old_key
        ) {
          throw new Error("R6 改键与已确认设备快照不匹配，已拒绝下发");
        }
        layer[keyIndex] = change.new_key;
      });
      const result = await this.syncProfileChanges(nextData, this.currentData, [
        "keymap",
      ]);
      this.currentData = result.baselineData;
      this.log("downloadJSON:success", {
        mode: "key-changes",
        count: changes.length,
      });
      return true;
    } catch (error) {
      this.emit("error", error);
      this.log("downloadJSON:failed", { error: error.message });
      console.error("R6 CDC downloadJSON failed", error);
      return false;
    }
  }

  async sendFullProfile(data, onProgress) {
    return this.trackFeature("profileJson", async () => {
      // Selection exports must keep ignored keyboard sections out of the JSON
      // transaction; their retained frame_data is not a request to resend it.
      const partialImport = Boolean(data?.__r6PartialImport);
      const downloadData = partialImport ? partialLedDownloadData(data) : data;
      if (this.deviceInfo?.fullJsonSupported === false) {
        return this.sendCompatibleFullProfile(
          downloadData,
          partialImport,
          onProgress
        );
      }
      const jsonFeatures = partialImport
        ? [
            "pageControl",
            "pageRgb",
            "axisRgb",
            "carLight",
            ...(Number(downloadData.key_layer?.valid) === 1
              ? ["keyLayer"]
              : []),
          ]
        : R6_MASTER_JSON_FEATURES;
      const result = await this.cdc.sendJsonConfig(
        downloadData,
        onProgress,
        jsonFeatures
      );
      const macroFrames =
        partialImport || Number(downloadData.key_layer?.valid) === 2
          ? []
          : buildR6Download(downloadData, ["macro"]).frames;
      if (macroFrames.length) {
        await this.trackFeature("macro", () =>
          this.cdc.sendMacroFrames(macroFrames)
        );
      }
      this.currentData = data;
      return result;
    });
  }

  async sendCompatibleFullProfile(data, partialImport, onProgress) {
    if (this.cdc.jsonTransactionBlocked || this.cdc.jsonTransactionUncertain) {
      throw new Error("上次 JSON 同步状态未确认，请重新连接设备后再导入配置");
    }
    if (!this.currentData) {
      throw new Error("请先同步设备配置，再导入 JSON 文件");
    }

    const profileData = {
      ...data,
      product_info: this.currentData.product_info ?? data.product_info,
      page_data: (data.page_data || []).map((page) => {
        const profilePage = { ...page };
        delete profilePage.page_control;
        return profilePage;
      }),
    };
    const lightingApply = Boolean(data.__r6LightingApply);
    const pageFeatureScope = Object.fromEntries(
      profileData.page_data.flatMap((page, index) => {
        const pageFeatures = partialImport
          ? [
              ...(isEnabledSection(page.word_page?.valid) ? ["pageText"] : []),
              ...(isEnabledSection(page.frames?.valid) ? ["pageRgb"] : []),
              ...(isEnabledSection(page.keyframes?.valid) ? ["axisRgb"] : []),
              ...(isEnabledSection(page.spotlight_frames?.valid)
                ? ["carLight"]
                : []),
            ]
          : [...PAGE_SYNC_FEATURES];
        if (lightingApply && !pageFeatures.includes("pageControl")) {
          pageFeatures.unshift("pageControl");
        }
        if (
          pageFeatures.length &&
          isEnabledSection(page.valid) &&
          !pageFeatures.includes("pageControl")
        ) {
          pageFeatures.unshift("pageControl");
        }
        return pageFeatures.length
          ? [[Number(page.page_index ?? index), pageFeatures]]
          : [];
      })
    );
    const featureScope = partialImport
      ? PAGE_SYNC_FEATURES.filter((feature) =>
          Object.values(pageFeatureScope).some((features) =>
            features.includes(feature)
          )
        )
      : [...PAGE_SYNC_FEATURES, "macro", "pressHold", "keyFunctions"];
    await this.syncProfileChanges(
      profileData,
      this.currentData,
      featureScope,
      onProgress,
      undefined,
      pageFeatureScope
    );

    if (!partialImport || Number(data.key_layer?.valid) === 1) {
      const keyLayerData = {
        ...profileData,
        key_layer: { ...profileData.key_layer, valid: 1 },
      };
      const keyLayerFrames = buildR6Download(keyLayerData, ["keyLayer"]).frames;
      await this.trackFeature("keyLayer", () =>
        this.cdc.sendKeyLayerFrames(
          keyLayerFrames,
          Number(keyLayerData.key_layer.layer_num) || 0,
          onProgress
        )
      );
    }

    this.currentData = profileData;
    return true;
  }

  async syncProfileChanges(
    nextData,
    previousData,
    featureScope,
    onProgress,
    pageIndexScope,
    pageFeatureScope
  ) {
    if (
      pageIndexScope !== undefined &&
      (!Number.isInteger(pageIndexScope) ||
        !(nextData.page_data || []).some(
          (page, index) => Number(page.page_index ?? index) === pageIndexScope
        ))
    ) {
      throw new Error(`R6 LED 页面索引无效：${pageIndexScope}`);
    }
    if (
      pageFeatureScope !== undefined &&
      (!Array.isArray(featureScope) ||
        !pageFeatureScope ||
        typeof pageFeatureScope !== "object" ||
        Array.isArray(pageFeatureScope))
    ) {
      throw new TypeError("R6 页面功能范围必须是对象");
    }
    Object.entries(pageFeatureScope || {}).forEach(([index, features]) => {
      const pageIndex = Number(index);
      if (
        !Number.isInteger(pageIndex) ||
        !(nextData.page_data || []).some(
          (page, pagePosition) =>
            Number(page.page_index ?? pagePosition) === pageIndex
        )
      ) {
        throw new Error(`R6 LED 页面索引无效：${index}`);
      }
      if (
        !Array.isArray(features) ||
        !features.length ||
        features.some(
          (feature) =>
            !PAGE_SYNC_FEATURES.includes(feature) ||
            !featureScope.includes(feature)
        )
      ) {
        throw new TypeError(`R6 LED 页面功能范围无效：${index}`);
      }
    });
    const fullDelta = diffR6Profile(previousData, nextData);
    const delta = featureScope
      ? scopedDelta(fullDelta, featureScope, pageIndexScope, pageFeatureScope)
      : fullDelta;
    if (delta.unsupportedSections.length) {
      throw new Error(
        `R6 CDC 暂无增量协议：${delta.unsupportedSections.join(", ")}`
      );
    }
    const sentFeatures = [];
    this.log("profile-delta:start", {
      keyChanges: delta.keyChanges.length,
      layerStructureChanged: delta.layerStructureChanged,
      macroChanged: delta.macroChanged,
      pressHoldChanged: delta.pressHoldChanged,
      keyFunctionsChanged: delta.keyFunctionsChanged,
      pageChanges: delta.pageChanges.map((change) => change.pageIndex),
      featureScope: featureScope || "all",
      pageIndexScope,
      pageFeatureScope,
    });

    let keyLayerFrames;

    if (delta.macroChanged) {
      const macroData = macroDataWithRemovedSlots(
        previousData.macro_key,
        nextData
      );
      const macroFrames = buildR6Download(macroData, ["macro"]).frames;
      await this.trackFeature("macro", () =>
        this.cdc.sendMacroFrames(macroFrames)
      );
      sentFeatures.push("macro");
    }

    if (delta.pressHoldChanged) {
      const holdFrames = changedPressHoldFrames(
        previousData.tab_key,
        nextData.tab_key
      );
      await this.trackFeature("pressHold", () =>
        this.cdc.sendPressHoldFrames(holdFrames)
      );
      sentFeatures.push("pressHold");
    }

    // ponytail: large diffs use the bulk path to avoid hundreds of per-key
    // ACK waits; raise only after a verified device-side bulk ACK protocol.
    const fullKeyLayerWrite =
      delta.layerStructureChanged ||
      (delta.keyLayerChanged &&
        (!delta.keyChanges.length || delta.keyChanges.length > 64));
    if (fullKeyLayerWrite) {
      const layerData = {
        ...nextData,
        key_layer: { ...nextData.key_layer, valid: 1 },
      };
      keyLayerFrames = buildR6Download(layerData, ["keyLayer"]).frames;
      await this.trackFeature("keyLayer", () =>
        this.cdc.sendKeyLayerFrames(
          keyLayerFrames,
          nextData.key_layer.layer_num,
          onProgress
        )
      );
      sentFeatures.push("keyLayer");
    } else if (delta.keyChanges.length) {
      // 06 0B 单项改键是写入即返回，不等待 ACK，不走 JSON 整包。
      await this.trackFeature("keymap", () =>
        this.cdc.sendKeyChanges(delta.keyChanges, onProgress)
      );
      sentFeatures.push("keymap");
    }

    if (delta.keyFunctionsChanged) {
      const functionFrames = buildR6Download(nextData, ["keyFunctions"]).frames;
      await this.trackFeature("keyFunctions", () =>
        this.cdc.sendKeyFunctionFrames(functionFrames)
      );
      sentFeatures.push("keyFunctions");
    }

    let verifiedData = nextData;
    const pageFeatures = [
      "pageControl",
      "pageText",
      "pageRgb",
      "axisRgb",
      "carLight",
    ].filter((feature) => delta.pageChanges.some((change) => change[feature]));
    if (pageFeatures.length) {
      if (
        this.cdc.jsonTransactionBlocked ||
        this.cdc.jsonTransactionUncertain
      ) {
        verifiedData = restoreSkippedPageFeatures(
          previousData,
          nextData,
          delta.pageChanges
        );
        this.log("profile-delta:json-skipped", {
          features: pageFeatures,
          reason: "json-transaction-uncertain",
        });
      } else {
        const fullJsonLedDownload = requiresFullJsonLedDownload(
          this.deviceInfo,
          pageFeatures
        );
        const partialData = fullJsonLedDownload
          ? pageFeatureScope && !hasAllR6LedEffects(pageFeatureScope)
            ? scopeFullJsonLightingData(nextData, pageFeatureScope)
            : nextData
          : pageDeltaData(
              nextData,
              previousData,
              delta.pageChanges,
              pageFeatures,
              pageIndexScope,
              pageFeatureScope
            );
        // AM Master keeps macros out of the R-series JSON transaction; 06 05
        // is sent only by the dedicated macro path.
        const jsonFeatures = fullJsonLedDownload
          ? R6_MASTER_JSON_FEATURES
          : pageFeatures;
        await this.trackFeature(pageFeatures, () =>
          this.cdc.sendJsonConfig(partialData, onProgress, jsonFeatures)
        );
        sentFeatures.push(...pageFeatures);
      }
    }

    let baselineData = featureScope
      ? mergeScopedBaseline(
          previousData,
          nextData,
          verifiedData,
          featureScope,
          pageIndexScope,
          pageFeatureScope
        )
      : verifiedData;
    if (
      (this.cdc.jsonTransactionBlocked || this.cdc.jsonTransactionUncertain) &&
      pageFeatures.length
    ) {
      baselineData = restoreSkippedPageFeatures(
        previousData,
        baselineData,
        delta.pageChanges
      );
    }
    this.currentData = baselineData;
    this.log("profile-delta:sent", {
      features: sentFeatures,
      writeOnlyFeatures: sentFeatures.filter((feature) =>
        ["keymap", "keyLayer", "macro", "pressHold"].includes(feature)
      ),
    });
    return { sentFeatures, data: verifiedData, baselineData, readback: {} };
  }

  async syncJSON(baseData) {
    this.deviceInfo = null;
    try {
      const freshConnection = this.isFreshConnection;
      this.log("syncJSON:start", { readback: "legacy" });
      const productId = await this.cdc.queryProductId();
      const compatibility = getR6ProductCompatibility(productId);
      if (!compatibility.cdcSupported) {
        throw new Error(
          `R6 CDC 设备型号不匹配，检测到 ${productId || "未知设备"}`
        );
      }
      const timeSync = { attempted: true, success: false };
      try {
        await this.trackFeature("timeSync", () => this.cdc.syncTime());
        timeSync.success = true;
      } catch (error) {
        // AM Master logs a failed clock update and continues connecting.
        timeSync.error = error.message;
        this.log("time-sync:failed", { productId, error: error.message });
      }
      const [productInfo, pageCount] = await Promise.all([
        this.cdc.queryProductInfo(),
        this.cdc.queryPageCount(),
      ]);
      this.deviceInfo = {
        productId,
        productModel: compatibility.model,
        productFamily: compatibility.family,
        cdcSupported: compatibility.cdcSupported,
        audioSupported: compatibility.audioSupported,
        firmwareSupported: compatibility.firmwareSupported,
        productInfo,
        pageCount,
        timeSync,
      };
      let snapshot = preserveProfilePages(baseData || {});
      let keyLayerReadback = false;
      let macroReadback = false;
      let pressHoldReadback = false;
      const readbackFeatures = createReadbackStatus();

      const keyLayerAttempted = typeof this.cdc.readKeyLayer === "function";
      const macroAttempted = typeof this.cdc.readMacro === "function";
      const pressHoldAttempted = typeof this.cdc.readPressHold === "function";
      const keyLayer = await this.readKeyLayerBestEffort(
        snapshot,
        !freshConnection
      );
      snapshot = keyLayer.data;
      keyLayerReadback = keyLayer.keyLayerReadback;

      const macro = await this.readMacroBestEffort(snapshot);
      snapshot = macro.data;
      macroReadback = macro.macroReadback;

      const pressHold = await this.readPressHoldBestEffort(snapshot);
      snapshot = pressHold.data;
      pressHoldReadback = pressHold.pressHoldReadback;

      markReadback(
        readbackFeatures,
        "keyLayer",
        keyLayerReadback,
        keyLayerAttempted
      );
      markReadback(readbackFeatures, "macroKey", macroReadback, macroAttempted);
      markReadback(
        readbackFeatures,
        "pressHold",
        pressHoldReadback,
        pressHoldAttempted
      );
      const info = {
        ...this.deviceInfo,
        readbackSupported: false,
        jsonReadbackAttempted: false,
        scopedJsonReadbackAttempted: false,
        jsonReadbackComplete: false,
        readbackMode: "legacy",
        readbackFeatures,
        keyLayerReadback,
        macroReadback,
        pressHoldReadback,
        unsupportedReadback: [],
      };

      this.currentData = snapshot;
      this.deviceInfo = info;
      this.isFreshConnection = false;
      this.log("syncJSON:complete", {
        productId,
        productFamily: compatibility.family,
        pageCount,
        readbackMode: "legacy",
        readbackFeatures,
      });
      this.emit("deviceInfo", info);
      this.emit("data", snapshot);
      return { info, data: snapshot };
    } catch (error) {
      this.emit("error", error);
      this.log("syncJSON:failed", { error: error.message });
      console.error("R6 CDC syncJSON failed", error);
      return null;
    }
  }

  async readKeyLayerBestEffort(baseData, preserveEmptyValues = true) {
    if (typeof this.cdc.readKeyLayer !== "function") {
      return { data: baseData, keyLayerReadback: false };
    }
    try {
      const layerData = await this.cdc.readKeyLayer();
      return {
        data: {
          ...baseData,
          key_layer: {
            ...baseData.key_layer,
            valid: 1,
            layer_num: layerData.length,
            // 部分固件的 06 09 返回带空占位，不能覆盖本地已有键位。
            layer_data: mergeKeyLayerData(
              baseData,
              layerData,
              preserveEmptyValues
            ),
          },
        },
        keyLayerReadback: true,
      };
    } catch (error) {
      this.log("readback:key-layer-skipped", { error: error.message });
      return { data: baseData, keyLayerReadback: false };
    }
  }

  async readMacroBestEffort(baseData) {
    if (typeof this.cdc.readMacro !== "function") {
      return { data: baseData, macroReadback: false };
    }
    try {
      const macroData = await this.cdc.readMacro();
      return {
        data: { ...baseData, ...macroData },
        macroReadback: true,
      };
    } catch (error) {
      this.log("readback:macro-skipped", { error: error.message });
      return { data: baseData, macroReadback: false };
    }
  }

  async readPressHoldBestEffort(baseData) {
    if (typeof this.cdc.readPressHold !== "function") {
      return { data: baseData, pressHoldReadback: false };
    }
    try {
      const pressHoldData = await this.cdc.readPressHold(baseData);
      return {
        data: { ...baseData, ...pressHoldData },
        pressHoldReadback: true,
      };
    } catch (error) {
      this.log("readback:press-hold-skipped", { error: error.message });
      return { data: baseData, pressHoldReadback: false };
    }
  }

  async downloadFile(value) {
    try {
      const data = parseJson(value);
      if (!data || !data.name) throw new Error("缺少导出文件名");
      const blob = new Blob([JSON.stringify(data.json)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = data.name;
      anchor.click();
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      this.emit("error", error);
      console.error("R6 CDC downloadFile failed", error);
      return false;
    }
  }

  async syncMacroKey(value) {
    if (!this.currentData) {
      const error = new Error("R6 尚未完成设备配置同步");
      this.emit("error", error);
      return false;
    }
    try {
      // 兼容旧宏组件入口，但只同步宏差异，不回退到整份 profile JSON。
      const changes = parseJson(value);
      if (
        !changes ||
        typeof changes !== "object" ||
        Array.isArray(changes) ||
        !Array.isArray(changes.macro_key)
      ) {
        throw new TypeError("R6 宏同步参数必须包含 macro_key 数组");
      }
      const nextData = { ...this.currentData, ...changes };
      const result = await this.syncProfileChanges(nextData, this.currentData, [
        "macro",
      ]);
      this.currentData = result.baselineData;
      return true;
    } catch (error) {
      this.emit("error", error);
      this.log("macro-sync:failed", { error: error.message });
      console.error("R6 CDC macro sync failed", error);
      return false;
    }
  }
}
